import React, {useContext, useState} from 'react';
import {useInfiniteQuery} from '@tanstack/react-query';
import {colors} from 'patient-ping-remedy/packages/theme';
import {StandardTypography} from 'patient-ping-remedy/packages/typography';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAuthStore} from '@/store/auth_store';
import {useActiveRoster} from '@/store/roster_store';
import {useDebounce} from '@/hooks/useDebounce';
import {useInfiniteScroll} from '@/hooks/useInfiniteScroll';
import {FeatureToggle, Roster, User} from '@/api/dto/dto';
import {AssignedSection, ScrollableMenu, Search, Wrapper} from '@common/AssignedToList.styles';
import DisplayHelpers from '@/helpers/display_helpers';
import PermissionsHelpers from '@/helpers/permissions_helpers';
import {Feature, FeatureNew, FeaturePermissionLevel} from "@/api/dto/permissions";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";
import AssignedToItem from "@common/AssignedToItem";
import Loading from '@common/Loading';

export type AssignUserParams = {
  user: User;
  delete: boolean;
}

type Props = {
  assignUser?(params: AssignUserParams): Promise<unknown>;
  roster?: Roster | null;
  hidePopover?(): void;
  reAssignToAi?(): void;
  assignedUsers: User[];
  canActOnListForRoster?: boolean;
  canManageOnListForRoster?: boolean;
  assignedToAi?: boolean;
  setConfirmAIUnassignModalOpen?(value: boolean): void;
  isTask?: boolean;
  assigningToAiIsPending?: boolean;
  status?: string | null;
}

export default function AssignedToList(props: Props) {
  const { currentUser } = useAuthStore();
  const currentRoster = useActiveRoster();
  const { carecoApi } = useContext(CarecoApiContext);
  const {checkBooleanToggle} = useFeatureToggles();

  const [disabledAssignUser, setDisabledAssignUser] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);

  const effectiveRoster = props.roster ?? currentRoster;

  const { data: usersData, fetchNextPage, isFetching, isFetched, isLoading } = useInfiniteQuery({
    queryKey: ['usersAssigned', effectiveRoster?.id, debouncedSearchTerm],
    queryFn: ({ pageParam }) => carecoApi?.getAssignableUsers({
      rosterId: effectiveRoster!.id,
      name: debouncedSearchTerm,
      page: pageParam,
      pageSize: 10,
    }),
    enabled: !!carecoApi && !!effectiveRoster,
    staleTime: 300_000,
    placeholderData: (prev) => prev,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
    initialPageParam: 1,
  });

  const { ref, listElement } = useInfiniteScroll<HTMLDivElement>({
    fetching: isFetching,
    offset: '50px',
    selector: '[data-assigned-item]',
    onNextPage() {
      if (usersData?.pages.at(-1)?.data.users.length) {
        void fetchNextPage();
      }
    },
  });

  const handleAssignUser = async (user: User) => {
    if (disabledAssignUser[user.id]) {
      return;
    }

    setDisabledAssignUser((disabled) => ({ ...disabled, [user.id]: true }));

    try {
      const deleteUserAssignment = props.assignedUsers.some((userAssignment) => userAssignment.id === user.id);
      await props.assignUser?.({ user, delete: deleteUserAssignment });
    } catch {
      // This catch is required so that useMutation.onError is properly invoked
      // error handling should also be done there (e.g. ClientsTable)
    } finally {
      setDisabledAssignUser((disabled) => ({ ...disabled, [user.id]: false }));
    }
  };

  const users = usersData?.pages.flatMap((page) => page?.data.users ?? []) ?? [];

  const assignedToSelf = props.assignedUsers
    .some((userAssigned) => userAssigned.id === currentUser?.id);

  const userAssignmentsWithoutSelf = props.assignedUsers
    .filter((userAssignment) => userAssignment.id !== currentUser?.id);

  const userAssignmentsVisible = userAssignmentsWithoutSelf
    .filter((userAssignment) => debouncedSearchTerm && isFetched
      ? debouncedSearchTerm
        .split(' ')
        .every((search) =>
          [userAssignment.first_name, userAssignment.last_name, userAssignment.email]
            .some((predicate) => predicate.toLowerCase().includes(search.trim().toLowerCase())))
      : true);

  const usersVisible = users
    .filter((user) => user.id !== currentUser?.id)
    .filter((user) => !userAssignmentsVisible
      .some((userAssignment) => userAssignment.id === user.id));

  const canManageUsersForRoster = (checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS) ? 
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser!,
      effectiveRoster!,
      FeaturePermissionLevel.MANAGE,
      FeatureNew.ADMIN_MANAGEMENT,
    ): PermissionsHelpers.canForUserFeaturePermissionRoster(
      currentUser!,
          effectiveRoster!,
          FeaturePermissionLevel.MANAGE,
          Feature.USER_PERMISSIONS,
    ));
  const handleAiClick = async () => {
    if (props.assignedToAi) {
      props.setConfirmAIUnassignModalOpen?.(true);
      return;
    }
    props.reAssignToAi?.();
  };
  const disableAssignAi= props.assigningToAiIsPending
      || !props.canActOnListForRoster
      || ( !props.assignedToAi && !(props.status?.toLowerCase() === 'active'));

  const canAssignOthersForList = props.isTask
    ? (props.canActOnListForRoster || props.canManageOnListForRoster)
    : (props.canManageOnListForRoster && canManageUsersForRoster);
  return (
    <Wrapper ref={listElement}>
      <Search placeholder="Search" onChange={(e) => setSearchTerm(e.target.value)} />
      <ScrollableMenu onHide={props.hidePopover}>
        {props.isTask && props.reAssignToAi && (
          <AssignedToItem
            data-testid="assign-to-ai"
            user={DisplayHelpers.aiAgentUser}
            roster={currentRoster}
            assigned={!!props.assignedToAi}
            loading={!!props.assigningToAiIsPending}
            disabled={disableAssignAi}
            onAssign={handleAiClick}
          />
        )}
        {currentUser && (
          <AssignedToItem
            data-testid="assign-to-self"
            user={currentUser}
            roster={currentRoster}
            assigned={assignedToSelf}
            loading={disabledAssignUser[currentUser.id]}
            disabled={disabledAssignUser[currentUser.id]}
            blocked={!props.canActOnListForRoster}
            own
            onAssign={handleAssignUser}
          />
        )}
        {[...userAssignmentsVisible, ...usersVisible].map((userAssignment) => {
          const disabled = disabledAssignUser[userAssignment.id]
            || !canAssignOthersForList
            || !PermissionsHelpers.isBamboo(currentUser!)
              && PermissionsHelpers.isBamboo(userAssignment);

          return (
            <AssignedToItem
              key={userAssignment.id}
              user={userAssignment}
              roster={currentRoster}
              assigned={props.assignedUsers.some((userAssigned) => userAssigned.id === userAssignment.id)}
              loading={disabledAssignUser[userAssignment.id]}
              disabled={disabled}
              onAssign={handleAssignUser}
            />
          );
        })}
        {isLoading && <Loading iconSize="lg" />}
        <span ref={ref} />
      </ScrollableMenu>
      {userAssignmentsWithoutSelf.length + (props.assignedToAi ? 1 : 0) > 0 && (
        <AssignedSection>
          <StandardTypography color={colors.gray4}>
            {props.assignedUsers.length + (props.assignedToAi ? 1 : 0)} assigned
          </StandardTypography>
        </AssignedSection>
      )}
    </Wrapper>
  );
}
